import React, { useEffect, useMemo, useState } from "react";
import { X, ZoomIn } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Small clickable thumbnail that opens a full-screen modal preview
 * of the image the user just imported for OCR extraction. Uses a local
 * blob URL — the image never leaves the browser except for the OCR call
 * already handled by Dashboard.
 *
 * Renders nothing when `file` is null / undefined / not an image.
 */
export const ImagePreview = ({ file, testidPrefix = "image-preview" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const url = useMemo(() => {
    if (!file || !file.type?.startsWith("image/")) return null;
    return URL.createObjectURL(file);
  }, [file]);

  // Revoke the blob URL when the component unmounts or the file changes,
  // otherwise the browser keeps the image bytes in memory forever.
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  // Escape closes the modal on desktop.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!url) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testidPrefix}-thumbnail`}
        className="group relative w-20 h-20 flex-shrink-0 border border-border overflow-hidden bg-secondary/40 hover:border-[#002FA7] transition-colors"
        aria-label={t("image_preview.alt")}
      >
        <img
          src={url}
          alt={t("image_preview.alt")}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#002FA7]/0 group-hover:bg-[#002FA7]/40 flex items-center justify-center transition-colors">
          <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-[#002FA7]/85 flex items-center justify-center p-4 md:p-8"
          onClick={() => setOpen(false)}
          data-testid={`${testidPrefix}-modal`}
          role="dialog"
          aria-modal="true"
          aria-label={t("image_preview.modal_title")}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            data-testid={`${testidPrefix}-close`}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 flex items-center justify-center bg-white text-black hover:bg-[#FF2A00] hover:text-white transition-colors"
            aria-label={t("image_preview.close")}
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={url}
            alt={t("image_preview.alt")}
            className="max-w-full max-h-full object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default ImagePreview;
